// src/components/chat/CitationGroupControls.tsx

/**
 * Small controls shared by the three citation chips and tooltips when a chip
 * stands for several adjacent citations: the "+N" badge on the chip, the
 * "‹ 1/N ›" pager in the tooltip header, and the stacked pages the tooltip
 * pages through. A single citation renders none of them.
 */

import React from "react";

/**
 * "+N" after the chip label. Kept outside the truncated label so it is never clipped.
 * `style` lets a chip whose label is styled differently keep the badge in step with it.
 */
export const CitationCountBadge: React.FC<{ count: number; style?: React.CSSProperties }> = ({
  count,
  style,
}) => (
  <span
    className="citation-count-badge"
    style={{ flexShrink: 0, fontWeight: 600, color: "#757575", ...style }}
  >
    +{count}
  </span>
);

const pagerButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  padding: 0,
  border: "none",
  borderRadius: "4px",
  backgroundColor: "transparent",
  color: "#616161",
  fontSize: "14px",
  lineHeight: 1,
  cursor: "pointer",
};

/** "‹ 1/N ›" in the tooltip header. Wraps around at both ends. */
export const CitationTooltipPager: React.FC<{
  index: number;
  total: number;
  onPage: (delta: number) => void;
}> = ({ index, total, onPage }) => (
  <div
    className="citation-tooltip-pager"
    style={{
      display: "flex",
      alignItems: "center",
      gap: "2px",
      marginLeft: "auto",
      flexShrink: 0,
    }}
  >
    <button
      type="button"
      aria-label="Previous citation"
      onClick={() => onPage(-1)}
      style={pagerButtonStyle}
    >
      ‹
    </button>
    <span
      aria-live="polite"
      style={{
        minWidth: "28px",
        textAlign: "center",
        fontSize: "11px",
        fontWeight: 500,
        color: "#616161",
      }}
    >
      {index + 1}/{total}
    </span>
    <button
      type="button"
      aria-label="Next citation"
      onClick={() => onPage(1)}
      style={pagerButtonStyle}
    >
      ›
    </button>
  </div>
);

/**
 * Every page of a merged chip's tooltip rendered in the same grid cell, with
 * only the current one visible. The cell takes the width and height of the
 * largest page, so the tooltip keeps one size while paging: its position is
 * computed once on open, and a page that resized it would move the pager out
 * from under the pointer, which closes a hover tooltip.
 */
export const CitationTooltipPages: React.FC<{
  index: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ index, children, style }) => (
  <div
    className="citation-tooltip-pages"
    style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", ...style }}
  >
    {React.Children.map(children, (page, i) => (
      <div
        aria-hidden={i === index ? undefined : true}
        style={{
          gridArea: "1 / 1",
          minWidth: 0,
          visibility: i === index ? "visible" : "hidden",
        }}
      >
        {page}
      </div>
    ))}
  </div>
);
